import React, { useState, useEffect, useCallback } from "react";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { Link } from "react-router-dom";

import Navbar from "../Navbar/Navbar";

// Interface for the slide content
interface SlideContent {
  image: string;
  mobileImage?: string; // Add optional mobile image property
  title: string;
  description: string;
  buttonLabel: string;
  link: string;
}

interface SliderProps {
  slides: SlideContent[];
  screenSize: { width: number };
}

const HomeSlider: React.FC<SliderProps> = ({ slides, screenSize }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToNext = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [slides.length, isTransitioning]);

  const goToPrev = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [slides.length, isTransitioning]);

  const goToSlide = useCallback(
    (index: number) => {
      if (isTransitioning || index === currentSlide) return;
      setIsTransitioning(true);
      setCurrentSlide(index);
      setTimeout(() => setIsTransitioning(false), 500);
    },
    [currentSlide, isTransitioning]
  );

  // Auto-play functionality
  useEffect(() => {
    const interval = setInterval(goToNext, 10000);
    return () => clearInterval(interval);
  }, [goToNext]);

  // Handle touch events for mobile swiping
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrev();
    }
  };

  // Function to get the appropriate image for current screen size
  const getImageForSlide = (slide: SlideContent, isMobile: boolean) => {
    if (isMobile && slide.mobileImage) {
      return slide.mobileImage;
    }
    return slide.image;
  };

  const isMobile = screenSize.width < 768; // Tailwind's md breakpoint

  return (
    <div className="relative">
      {/* Mobile Navbar */}
      <div className="block md:hidden">
        <Navbar />
      </div>

      {/* Main Slider Container */}
      <div
        className="relative w-full aspect-[4/3] sm:aspect-[3/2] md:aspect-[21/9] overflow-hidden md:h-screen"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Slides Container */}
        <div
          className="flex w-full h-full transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slides.map((slide, index) => (
            <div key={index} className="relative w-full h-full flex-shrink-0">
              <img
                className="w-full h-full object-cover object-center"
                src={getImageForSlide(slide, isMobile)}
                alt={`Slide ${index + 1}`}
                loading={index === 0 ? "eager" : "lazy"}
              />
              <div className="absolute inset-0 bg-black opacity-5" />
            </div>
          ))}
        </div>

        {/* Navigation Arrows - Desktop Only */}
        <button
          onClick={goToPrev}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white z-50 opacity-50 hover:opacity-100 transition-opacity duration-300 hidden md:block"
          disabled={isTransitioning}
        >
          <BsChevronLeft fontSize={50} />
        </button>

        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white z-50 opacity-50 hover:opacity-100 transition-opacity duration-300 hidden md:block"
          disabled={isTransitioning}
        >
          <BsChevronRight fontSize={50} />
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-50">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                currentSlide === index
                  ? "bg-white opacity-100"
                  : "bg-white opacity-50 hover:opacity-75"
              }`}
            />
          ))}
        </div>

        {/* Desktop Navbar */}
        <div className="hidden md:block absolute z-50 top-0 left-0 w-full">
          <Navbar />
        </div>

        {/* Slide Content */}
        <div className="absolute z-20 inset-0">
          {/* Centering container - Modified for left alignment on all screens */}
          <div className="h-full md:mt-[50px] flex items-center justify-start px-4 md:px-[10%] mt-[0px] sm:mt-[25px]">
            {/* Content - Modified for left alignment on all screens */}
            <div className="text-left w-full max-w-none">
              {/* Title */}
              <h1 className="text-white font-black uppercase leading-none opacity-100 font-dm-sans text-[30px] md:text-[90px] md:leading-none mb-4 md:mb-6">
                {slides[currentSlide].title}
              </h1>

              {/* Description */}
              <p className="text-white font-black opacity-100 text-[18px] md:text-[40px] mb-6 md:mb-8 leading-tight md:leading-none">
                {slides[currentSlide].description}
              </p>

              {/* Button */}
              <div className="flex justify-start">
                <Link to={slides[currentSlide].link}>
                  <button className="bg-white text-[#005D6E] font-bold px-6 py-2 text-base md:px-10 md:py-4 md:text-xl rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300">
                    {slides[currentSlide].buttonLabel}
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeSlider;

// import React, { useState, useEffect, useCallback } from "react";
// import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
// import { Link } from "react-router-dom";
// import PrimaryButton from "../Button/PrimaryButton";
// import Navbar from "../Navbar/Navbar";

// // Interface for the slide content
// interface SlideContent {
//   image: string;
//   title: string;
//   description: string;
//   buttonLabel: string;
//   link: string;
// }

// interface SliderProps {
//   slides: SlideContent[];
//   screenSize: { width: number };
// }

// const HomeSlider: React.FC<SliderProps> = ({ slides, screenSize }) => {
//   const [currentSlide, setCurrentSlide] = useState(0);
//   const [isTransitioning, setIsTransitioning] = useState(false);

//   const goToNext = useCallback(() => {
//     if (isTransitioning) return;
//     setIsTransitioning(true);
//     setCurrentSlide((prev) => (prev + 1) % slides.length);
//     setTimeout(() => setIsTransitioning(false), 500);
//   }, [slides.length, isTransitioning]);

//   const goToPrev = useCallback(() => {
//     if (isTransitioning) return;
//     setIsTransitioning(true);
//     setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
//     setTimeout(() => setIsTransitioning(false), 500);
//   }, [slides.length, isTransitioning]);

//   const goToSlide = useCallback(
//     (index: number) => {
//       if (isTransitioning || index === currentSlide) return;
//       setIsTransitioning(true);
//       setCurrentSlide(index);
//       setTimeout(() => setIsTransitioning(false), 500);
//     },
//     [currentSlide, isTransitioning]
//   );

//   // Auto-play functionality
//   useEffect(() => {
//     const interval = setInterval(goToNext, 10000);
//     return () => clearInterval(interval);
//   }, [goToNext]);

//   // Handle touch events for mobile swiping
//   const [touchStart, setTouchStart] = useState<number | null>(null);
//   const [touchEnd, setTouchEnd] = useState<number | null>(null);

//   const minSwipeDistance = 50;

//   const onTouchStart = (e: React.TouchEvent) => {
//     setTouchEnd(null);
//     setTouchStart(e.targetTouches[0].clientX);
//   };

//   const onTouchMove = (e: React.TouchEvent) => {
//     setTouchEnd(e.targetTouches[0].clientX);
//   };

//   const onTouchEnd = () => {
//     if (!touchStart || !touchEnd) return;
//     const distance = touchStart - touchEnd;
//     const isLeftSwipe = distance > minSwipeDistance;
//     const isRightSwipe = distance < -minSwipeDistance;

//     if (isLeftSwipe) {
//       goToNext();
//     } else if (isRightSwipe) {
//       goToPrev();
//     }
//   };

//   return (
//     <div className="relative">
//       <div className="block md:hidden">
//         <Navbar />
//       </div>

//       {/* Custom Slider Container */}
//       <div
//         className="relative w-full h-[400px] md:h-[750px] overflow-hidden"
//         onTouchStart={onTouchStart}
//         onTouchMove={onTouchMove}
//         onTouchEnd={onTouchEnd}>
//         {/* Slides Container */}
//         <div
//           className="flex w-full h-full transition-transform duration-500 ease-in-out"
//           style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
//           {slides.map((slide, index) => (
//             <div key={index} className="relative w-full h-full flex-shrink-0">
//               <img
//                 className="w-full h-full object-center-bottom object-cover"
//                 src={slide.image}
//                 alt={`Slide ${index + 1}`}
//                 loading={index === 0 ? "eager" : "lazy"}
//               />
//               <div className="absolute inset-0 bg-black opacity-5" />
//             </div>
//           ))}
//         </div>

//         {/* Navigation Arrows - Desktop Only */}
//         <button
//           onClick={goToPrev}
//           className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white z-50 opacity-50 hover:opacity-100 transition-opacity duration-300 hidden md:block"
//           disabled={isTransitioning}>
//           <BsChevronLeft fontSize={50} />
//         </button>

//         <button
//           onClick={goToNext}
//           className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white z-50 opacity-50 hover:opacity-100 transition-opacity duration-300 hidden md:block"
//           disabled={isTransitioning}>
//           <BsChevronRight fontSize={50} />
//         </button>

//         {/* Dots Indicator */}
//         <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-50">
//           {slides.map((_, index) => (
//             <button
//               key={index}
//               onClick={() => goToSlide(index)}
//               className={`w-3 h-3 rounded-full transition-all duration-300 ${
//                 currentSlide === index
//                   ? "bg-white opacity-100"
//                   : "bg-white opacity-50 hover:opacity-75"
//               }`}
//             />
//           ))}
//         </div>

//         {/* Desktop Navbar Overlay */}
//         <div className="hidden md:block absolute z-50 top-0 left-0 w-full">
//           <Navbar />
//         </div>

//         {/* Content Overlay */}
//         <div className="absolute z-20 top-0 left-0 w-full h-full">
//           <div className="flex flex-col items-start justify-center md:justify-start md:mt-[200px] px-4 md:px-[10%] text-center md:text-left h-full">
//             <p className="text-white font-black uppercase leading-none opacity-100 font-dm-sans text-[30px] md:text-[90px] md:leading-none text-left w-[70%] mb-4 md:mb-0">
//               {slides[currentSlide].title}
//             </p>

//             <div className="flex flex-col items-center justify-center text-center mt-[10%] md:mt-[2%]">
//               <div className="w-full">
//                 <p className="text-white font-black opacity-100 text-[18px] md:text-[40px] mb-[5%] md:mb-[2%] leading-none md:leading-none">
//                   {slides[currentSlide].description}
//                 </p>
//               </div>

//               <div className="w-full">
//                 <Link to={slides[currentSlide].link}>
//                   <PrimaryButton label={slides[currentSlide].buttonLabel} />
//                 </Link>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default HomeSlider;
